import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { JobEvent } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { ErrorBanner, Spinner } from './common'

/**
 * «Installa agente» on a server or VM: the chosen probe connects to the
 * machine (SSH on Linux, WinRM on Windows) and installs the CENSO agent, the
 * same installer a person would paste. The agent is created for this CI and
 * introduces itself as soon as it starts. See agent_remote_install_service.
 */
export function InstallaAgente({ ciId, onChiudi }: { ciId: string; onChiudi: () => void }) {
  const opzioni = useAsync(() => api.ciInstallAgentOptions(ciId), [ciId])
  const [sistema, setSistema] = useState<'linux' | 'windows' | ''>('')
  const [sonda, setSonda] = useState('')
  const [credenziale, setCredenziale] = useState('')
  const [https, setHttps] = useState(true)
  const [forza, setForza] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [avviato, setAvviato] = useState<{ job_id: string; agent_id: string; agent_name: string } | null>(null)
  const [stato, setStato] = useState<{ status: string; error_message: string | null } | null>(null)
  const [eventi, setEventi] = useState<JobEvent[]>([])

  const o = opzioni.data
  useEffect(() => {
    if (!o) return
    setSistema((s) => s || o.os_family || '')
    const capaci = o.probes.filter((p) => p.capable && p.online)
    setSonda((s) => s || (capaci.find((p) => p.discovered_it) ?? capaci[0])?.id || '')
  }, [o])

  // Follows the job until it ends.
  useEffect(() => {
    if (!avviato) return
    let fermo = false
    const giro = async () => {
      try {
        const r = await api.jobReport(avviato.job_id)
        if (fermo) return
        setStato({ status: r.job.status, error_message: r.job.error_message })
        setEventi(r.events)
        if (!['succeeded', 'failed', 'partial'].includes(r.job.status)) setTimeout(giro, 3000)
      } catch {
        if (!fermo) setTimeout(giro, 5000)
      }
    }
    void giro()
    return () => { fermo = true }
  }, [avviato])

  const credenziali = (o?.credentials ?? []).filter((c) =>
    !sistema || (sistema === 'linux' ? ['ssh_key', 'ssh_password'].includes(c.kind) : c.kind === 'windows'))

  async function avvia() {
    setErrore(null)
    try {
      setAvviato(await api.ciInstallAgent(ciId, {
        probe_id: sonda, credential_id: credenziale, os_family: sistema || null, winrm_tls: https, force: forza,
      }))
    } catch (e) { setErrore(e) }
  }

  const finito = stato && ['succeeded', 'failed', 'partial'].includes(stato.status)
  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Installa l’agente CENSO')}>
      <div className="dob-modal__scrim" onClick={onChiudi} />
      <div className="dob-modal__pannello" style={{ width: 'min(640px, 100%)' }}>
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{t('Installa l’agente CENSO')}</h3>
            <p className="dob-modal__sotto">
              {t('La sonda scelta si collega alla macchina — SSH su Linux, WinRM su Windows — e installa l’agente, lo stesso installer che si incollerebbe a mano. L’agente nasce legato a questo CI e si annuncia appena parte.')}
            </p>
          </div>
          <button className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>
        </div>
        <ErrorBanner error={errore ?? opzioni.error} />
        {opzioni.loading && !o ? <Spinner /> : o && !avviato && (
          <div className="form-grid" style={{ display: 'grid', gap: 12 }}>
            {!o.address && <div className="banner banner--warn">{t('Il CI non ha un indirizzo di gestione: la sonda non saprebbe a cosa collegarsi.')}</div>}
            {o.existing_agent && (
              <div className="banner banner--info">
                {t('Su questo CI c’è già un agente attivo: «{nome}».', { nome: o.existing_agent.name })}{' '}
                <label className="checkbox"><input type="checkbox" checked={forza} onChange={(e) => setForza(e.target.checked)} /> {t('Reinstalla comunque')}</label>
              </div>
            )}
            <label className="field">
              <span className="field__label">{t('Sistema')}</span>
              <select value={sistema} onChange={(e) => { setSistema(e.target.value as 'linux' | 'windows' | ''); setCredenziale('') }}>
                <option value="">{t('— scegli —')}</option>
                <option value="linux">{t('Linux (SSH)')}</option>
                <option value="windows">{t('Windows (WinRM)')}</option>
              </select>
              {!o.os_family && <span className="field__hint">{t('Il sistema del CI non è noto: sceglilo.')}</span>}
            </label>
            <label className="field">
              <span className="field__label">{t('Sonda che installa')}</span>
              <select value={sonda} onChange={(e) => setSonda(e.target.value)}>
                <option value="">{t('— scegli —')}</option>
                {o.probes.map((p) => (
                  <option key={p.id} value={p.id} disabled={!p.capable || !p.online}>
                    {p.name}
                    {p.discovered_it ? ` · ${t('ha scoperto il CI')}` : ''}
                    {!p.online ? ` · ${t('non in linea')}` : !p.capable ? ` · ${t('da aggiornare')}` : ''}
                    {p.capable && p.relay ? ` · ${t('l’agente consegnerà a questa sonda')}` : ''}
                  </option>
                ))}
              </select>
              <span className="field__hint">
                {t('Se la sonda ha il ricevitore delle consegne acceso, la macchina scarica l’installer da lei e le consegna l’inventario; altrimenti usa il Control Plane.')}
              </span>
            </label>
            <label className="field">
              <span className="field__label">{t('Credenziale')}</span>
              <select value={credenziale} onChange={(e) => setCredenziale(e.target.value)} disabled={!sistema}>
                <option value="">{t('— scegli —')}</option>
                {credenziali.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.kind}</option>)}
              </select>
              <span className="field__hint">
                {sistema === 'windows'
                  ? t('Un amministratore della macchina. WinRM deve essere attivo (HTTPS sulla 5986, o HTTP sulla 5985).')
                  : t('root, oppure un utente con sudo senza password: l’installazione non può rispondere a una richiesta di password.')}
              </span>
            </label>
            {sistema === 'windows' && (
              <label className="checkbox"><input type="checkbox" checked={https} onChange={(e) => setHttps(e.target.checked)} /> {t('WinRM in HTTPS (5986)')}</label>
            )}
            <div className="toolbar">
              <button disabled={!sonda || !credenziale || !sistema || !o.address || (Boolean(o.existing_agent) && !forza)} onClick={avvia}>
                {t('Installa')}
              </button>
              <Link to="/credentials">{t('Gestisci le credenziali')}</Link>
            </div>
          </div>
        )}
        {avviato && (
          <div>
            <p>
              {t('Installazione avviata: agente «{nome}».', { nome: avviato.agent_name })}
            </p>
            {eventi.length > 0 && (
              <ul className="text-muted" style={{ fontSize: 13 }}>
                {eventi.map((e, i) => (
                  <li key={i}>
                    {e.message}
                    {typeof e.details?.uscita === 'string' && e.details.uscita && (
                      <pre className="mono" style={{ whiteSpace: 'pre-wrap', maxHeight: 220, overflow: 'auto', fontSize: 11 }}>{e.details.uscita}</pre>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {!finito && <Spinner label={t('La sonda sta installando… (stato: {s})', { s: stato?.status ?? 'pending' })} />}
            {stato?.status === 'succeeded' && (
              <div className="banner banner--ok">
                {t('Agente installato. Compare in Agenti d’inventario e consegna il primo inventario entro pochi minuti.')}{' '}
                <Link to="/inventory-agents">{t('Apri gli agenti')}</Link>
              </div>
            )}
            {finito && stato?.status !== 'succeeded' && (
              <div className="banner banner--error">
                {t('Installazione non riuscita: {motivo}', { motivo: stato?.error_message ?? stato?.status ?? '' })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
